import axios from 'axios'
import { sum, values } from 'lodash'
import Toaster from '@/utils/toaster'
import get from 'lodash/fp/get'
import imgCompressor from '@/utils/imgCompressor'

const uploadUrl = 'https://api.cloudinary.com/v1_1/do4tedxg6/upload'
const defaultPreset = 'tcu2ksyf'
export const cloudinaryAxios = axios.create({ withCredentials: true })

export async function uploadToCloudinary(
  files,
  { onUploadingChange, onUploadProgress } = {},
  config
) {
  onUploadingChange = onUploadingChange || (() => undefined)
  onUploadProgress = onUploadProgress || (() => undefined)

  const filesProgress = {}
  let totalSize = 0
  const { compress = true, folder = '', uploadPreset = defaultPreset } = config
  onUploadingChange(true)
  onUploadProgress(0.01)

  const promises = files.map(async (f, index) => {
    let shouldImprove = false
    if (Array.isArray(f)) {
      shouldImprove = true
      f = f[0]
    }
    const file =
      f.type?.includes('image') && compress ? await imgCompressor(f) : f
    totalSize += file?.size || 1
    filesProgress[index] = 0

    let uploadPresetParam = compress ? uploadPreset : 'original'
    if (f.type?.includes('video') || f.type?.includes('application/pdf')) {
      uploadPresetParam = defaultPreset
    }

    const formData = new FormData()
    formData.append('upload_preset', uploadPresetParam)

    // Keep existing path structure, but use shorter filenames
    const companyName = cleanString(window.config.company_name)
    const cleanFolder = folder ? cleanString(folder) : ''
    const folderPath = cleanFolder
      ? `tenants/${companyName}/${cleanFolder}`
      : `tenants/${companyName}`

    // Generate short random hash for filename (8 chars) - keeps URLs shorter
    const shortHash = Math.random().toString(36).substring(2, 10)

    formData.append('folder', folderPath)
    formData.append('public_id', shortHash)
    if (shouldImprove) {
      formData.set('upload_preset', 'ota-images')
    }

    return uploadEntirely(
      file,
      formData,
      index,
      filesProgress,
      totalSize,
      onUploadProgress
    )
  })

  return Promise.all(promises)
    .then(result =>
      result.map(r => {
        if (r.data.eager && r.data.eager.length) {
          return r.data.eager[0].secure_url
        }
        return r.data.secure_url
      })
    )
    .catch(error => {
      let errorMessage = get('response.data.error', error) || error.message
      if (
        typeof errorMessage === 'undefined' ||
        (typeof errorMessage === 'object' &&
          !Array.isArray(errorMessage) &&
          errorMessage !== null)
      ) {
        errorMessage = JSON.stringify(error)
      }
      Toaster.show([
        { type: 'error', text: `Image upload failed - ${errorMessage}` },
      ])
      throw error
    })
    .finally(() => onUploadingChange(false))
}

function updateProgress(filesProgress, totalSize, onUploadProgress) {
  const progress = Math.min(
    Math.round(sum(values(filesProgress)) * 100) / totalSize,
    100
  )
  onUploadProgress(progress)
}

async function uploadEntirely(
  file,
  formData,
  index,
  filesProgress,
  totalSize,
  onUploadProgress
) {
  formData.delete('public_id')
  formData.append('file', file)
  const uploadHeaders = {
    'Content-Type': `multipart/form-data; boundary=${formData._boundary}`,
  }

  return cloudinaryAxios.post(uploadUrl, formData, {
    headers: uploadHeaders,
    withCredentials: false,
    onUploadProgress: progressEvent => {
      filesProgress[index] = progressEvent.loaded
      updateProgress(filesProgress, totalSize, onUploadProgress)
    },
  })
}

function cleanString(str) {
  return str
    .normalize('NFD') // Decompose diacritics (e.g., "á" → "a" + combining accent)
    .replace(/[\u0300-\u036f]/g, '') // Remove combining diacritical marks
    .replace(/\p{Extended_Pictographic}/gu, '') // Remove emojis (not digits)
    .replace(/[?&#\\%<>+ /]/g, '') // Remove special characters including forward slash
}
